<template>
  <div class="page-demo-player">

    <div v-if="error" class="error-box">
      <div class="error-header">
<!--        <i class="icon error-icon">error_outline</i>-->
        <ErrorIcon style="height: 64px; padding-right: 2rem"/>
        <div>Ups, looks like something went terribly wrong. Here's the error message:</div>
      </div>
      <div class="error-content-box" @click="copyErrorToClipboard">
        <div class="error-content">
          {{error}}
        </div>
      </div>
      <div class="error-info">
        <div class="error-docs-header" v-if="errorDocs">
          Here is what we found in the documentation for this specific error.
          <div v-html="errorDocs" class="error-docs"></div>
        </div>
        Check the <router-link :to="{name: 'docs', query: {q:'clpp.Error#Code'}}">Error API</router-link> for more
        details on the error. You can also take a closer look the
        <router-link :to="{name: 'player.logs', query: subPageQuery}">Player Logs</router-link> to get more information.
      </div>
    </div>

    <div class="player-container">
      <Player :asset="asset" class="player"
              :fullscreen-thumbs="true"
              v-on:player="initPlayer($event.ppPlayer, $event.castProxy)"
              v-on:error="onError($event)"
      >
        <div slot="idle" class="info-idle">
          <BrowserIcon style="width: 100px;" v-if="this.$route.name !== 'player.config'"/>
          <DownArrow style="width: 128px;" v-else/>
        </div>
        <div slot="error" style="pointer-events: none; display: flex; align-items: center; justify-content: center; height: 100%;">
          <ErrorIcon style="width: 64px; padding-right: 2rem"/>
          <div>Uups</div>
        </div>
      </Player>

      <div v-if="asset.id" class="byline">
        <span class="id">{{asset.id}}</span>
        <div class="metadata" v-if="asset?.metadata?.byline" v-html="asset.metadata.byline"/>
      </div>
    </div>

    <div class="info-table-container">
      <TableStats v-if="hasConfig" :ppPlayerRef='player' class="info-table" />
      <div v-else class="info-text" style="display: flex; flex-direction: column; align-items: center">
        <div style="flex: 1 1 auto; text-align: left">
          <span>Get to know Castlabs’ <a href="https://castlabs.com/prestoplay/web-apps/" target="_blank">PRESTOplay for Web Apps</a>. Our powerful video playback technology for HTML5 platforms can be tested here.</span>
        </div>
      </div>
    </div>

    <div class="pp-toolbar pp-toolbar-transparent pp-sub-nav">
      <ul>
        <router-link-li :to="{name: 'player.demo', query: subPageQuery}">Demos</router-link-li>
        <router-link-li :to="{name: 'player.stats', query: subPageQuery}" :disabled="!hasConfig">Stats</router-link-li>
        <router-link-li :to="{name: 'player.logs', query: subPageQuery}" :disabled="!hasConfig">Logs</router-link-li>
        <router-link-li :to="{name: 'player.config', query: subPageQuery}" :disabled="!hasConfig">Configure</router-link-li>
      </ul>
    </div>
    <router-view :ppPlayerRef="player" :log="log" :events="events" :config="config"
      v-bind:tempQuery="tempQuery"
      v-on:updateTempQuery="tempQuery = $event"
    />
  </div>

</template>

<script>
import Vue from 'vue';
import Player from '../components/player/Player';
import TableStats from '../components/player/info/TableStats';
import { Docs } from '../utils/docs';
import ErrorIcon from '../assets/stream-error.svg';
import DownArrow from '../assets/down-arrow.svg';
import BrowserIcon from '../assets/browser-player.svg';
import { createPlayerConfig } from '../utils/player';
import load from '../utils/fetch';
import { convertFromAssetDb } from '../utils/assetdb_utils';
import RouterLinkLi from '../components/router/RouterLink.vue';

export default {
  name: 'page-demo-player',
  components: {
    TableStats,
    Player,
    ErrorIcon,
    DownArrow,
    BrowserIcon,
    RouterLinkLi
  },
  props: ['cfg', 'assetId', 'customId'],
  data () {
    return {
      tempQuery: '',
      player: null,
      log: '',
      error: null,
      events: [],
      config: null,
      eventTarget: {
        dispatchEvent: e => {
          const excluded = ['timeupdate', 'progress', 'videobufferchanged', 'audiobufferchanged'];
          if (excluded.indexOf(e.type) < 0) {
            this.pushEvent(e);
          }
        }
      }

    };
  },
  computed: {
    subPageQuery () {
      return {
        cfg: this.configString,
        assetId: this.assetId,
        customId: this.customId
      };
    },
    asset () {
      return clpp.utils.PlayerConfiguration.create(this.config);
    },
    configString () {
      const cfg = this.config || this.cfg;
      const string = JSON.stringify(createPlayerConfig(cfg, true));
      if (string === '{}') return undefined;
      return btoa(string);
    },
    hasConfig () {
      return !!(this.config && this.config.source && this.config.source.url);
    },
    showPlayer () {
      return !!(this.config && this.config.source && this.config.source.url) || this.$route.name === 'player.config';
    },
    errorDocs () {
      if (!this.error) return '';
      const docsEntry = Docs.findErrorByCode(this.error.code);
      if (docsEntry) {
        return Docs.description(docsEntry);
      }
      return '';
    }
  },
  watch: {
    cfg: function (newValue, oldValue) {
      this.config = newValue;
    },
    $route (to, from) {
      if (to.name === 'player') {
        this.$router.push({ name: 'player.demo', query: this.subPageQuery });
      }
    }
  },
  mounted () {
    if (!this.showPlayer && this.$route.name !== 'player.demo') {
      this.$router.push({ name: 'player.demo', query: this.subPageQuery });
    }
    if (!this.config && this.cfg) {
      this.config = this.cfg;
    }
    if (!this.configString && this.assetId) {
      this.loadAssetConfig();
    }
  },
  destroyed () {
    clpp.log.removeInterceptor(this.interceptor);
    if (this.player) {
      const pp = this.player.get();
      pp.stopForwardingEventsTo(this.eventTarget);
      pp.off(clpp.events.STATE_CHANGED, this.onStateChanged);
    }
  },
  methods: {
    async copyErrorToClipboard () {
      try {
        await navigator.clipboard.writeText(this.error.toString());
        Vue.$toast.open({
          message: 'Error copied to clipboard.',
          duration: 2000,
          position: 'top',
        });
      } catch (err) {
        console.error('Failed to copy to clipboard', err);
      }
    },
    onError (e) {
      console.error(e.detail);
      if (e.detail && e.detail.severity === clpp.Error.Severity.FATAL) {
        this.error = e.detail;
      }
    },
    onStateChanged (event) {
      const state = event.detail.currentState;
      if (state !== clpp.Player.State.ERROR) {
        this.error = null;
      }
    },
    initPlayer (player, castProxy) {
      this.player = player;
      if (this.player) {
        const pp = this.player.get();
        pp.forwardEventsTo(this.eventTarget);
        pp.on(clpp.events.STATE_CHANGED, this.onStateChanged);
        window.player = pp;
      }
      if (castProxy) {
        window.castProxy = castProxy;
      }
      clpp.log.addInterceptor(this.interceptor);
    },
    interceptor (level, args) {
      let line = '';
      for (let i = 0; i < args.length; i++) {
        const a = args[i];
        if (typeof a === 'object') {
          line += '\n' + JSON.stringify(a, null, 2) + ' ';
        } else {
          line += a + ' ';
        }
      }
      this.log = line + '\n' + this.log;
      if (this.log.length > 100000) {
        this.log = this.log.substr(0, 100000);
      }
    },
    pushEvent (e) {
      this.events.unshift({
        type: e.type,
        detail: e.detail,
        details: e.details,
        expirationTime: e.expirationTime,
        keySystem: e.keySystem
      });
      this.events.splice(100);
    },
    loadAssetConfig () {
      const assetUrl = `https://assets.castlabs.com/api/config/${this.assetId}/`;
      return load(assetUrl).then((response) => {
        return response.json();
      }).then((cfg) => {
        const c = convertFromAssetDb(cfg);
        if (c) {
          this.config = c.cfg;
        } else {
          this.message = 'Unable to load configuration!';
        }
      }).catch((e) => {
        this.message = 'Unable to load configuration: ' + e.message;
        console.log('Ups, something went wrong while loading the asset config', e);
      });
    }

  }
};
</script>

<style scoped lang="scss">

  @import "../style/color_";

  .page-demo-player {
    padding: 1rem;

    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;

  }

  .player-container {
    width: 400px;
    flex: 1 1 auto;
    max-width: 400px;
    margin: auto auto 0;
    padding-top: 8px;
  }

  .byline {
    position: relative;
    line-height: 24px;
    min-height: 24px;
  }
  .byline > .id {
    position: absolute;
    color: black;
    font-style: italic;
    opacity: 0.6;
  }
  .byline > .id::before {
    content: '#';
    opacity: 0.4;
  }
  .byline > .metadata {
    text-align: center;
    padding: 0 40px;
  }

  .info-idle {
    pointer-events: none;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;
    background-color: $color-background-content;
    color: $color-secondary;
  }

  .info-table-container {
    overflow: auto;
    margin: auto;

    scrollbar-width: thin;
  }

  .info-table {
    font-size: 1.3rem;
    flex: 0 1 auto;
    margin: auto;
    width: 320px;
  }

  .info-text {
    font-size: 1.5rem;
    flex: 1 1 auto;
    margin: auto;
    max-width: 320px;
  }

  .info-table-disabled {
    color: $color-secondary;
  }

  .pp-sub-nav {
    border-top: 1px solid $color-secondary;
    margin-top: 20px;
  }

  @media (min-width: 600px) {
    .page-demo-player {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      justify-content: space-between;
    }

    .info-table {
      width: 320px;
      flex: 0 1 auto;
      margin: auto;
    }

    .info-text {
      max-width: 400px;
      font-size: 1.6rem;
      flex: 1 1 auto;
      margin: auto;
    }

    .player-container {
      width: 400px;
      flex: 1 1 auto;
    }

  }

  @media (min-width: 865px) {
    .player {
      flex: 0 1 auto;
      /*margin: 32px;*/
    }
    .info-table {
      /*margin: 32px;*/
      /*min-width: 320px;*/
    }
  }
</style>
